import React from 'react';
import { safeJsonParse } from './jsonUtils.js';
import { RICOS_TEXT_COLOR_TYPE, RICOS_TEXT_HIGHLIGHT_TYPE, RICOS_FONT_SIZE_TYPE, RICOS_BOLD_TYPE, RICOS_ITALIC_TYPE, TEXT_COLOR_TYPE, TEXT_HIGHLIGHT_TYPE } from 'ricos-content';
import { isHexColor } from './colorUtils.js';
import { getBlocksFromContentState } from './innerRCEBlocksUtils.js';

const INLINE_STYLE_TYPES = {
  [RICOS_TEXT_COLOR_TYPE]: "FG",
  [RICOS_TEXT_HIGHLIGHT_TYPE]: "BG",
  [RICOS_FONT_SIZE_TYPE]: "font-size"
};
const RICOS_TO_DRAFT_TYPE = {
  [RICOS_TEXT_COLOR_TYPE]: TEXT_COLOR_TYPE,
  [RICOS_TEXT_HIGHLIGHT_TYPE]: TEXT_HIGHLIGHT_TYPE
};
const parseStyleByType = (style, type) => {
  const property = INLINE_STYLE_TYPES[type];
  const cssRule = safeJsonParse(style);
  return cssRule == null ? void 0 : cssRule[property];
};
const dynamicStyleParsers = {
  [RICOS_TEXT_COLOR_TYPE]: (style, styleSelectionPredicate) => {
    const color = parseStyleByType(style, RICOS_TEXT_COLOR_TYPE);
    if (styleSelectionPredicate) {
      const parsedStyle = color || style;
      return styleSelectionPredicate(parsedStyle) && parsedStyle;
    } else {
      return color || isHexColor(style) && style;
    }
  },
  [RICOS_TEXT_HIGHLIGHT_TYPE]: (style, styleSelectionPredicate) => {
    const color = parseStyleByType(style, RICOS_TEXT_HIGHLIGHT_TYPE);
    return styleSelectionPredicate ? styleSelectionPredicate(color) && color : color;
  },
  [RICOS_FONT_SIZE_TYPE]: (style) => parseStyleByType(style, RICOS_FONT_SIZE_TYPE)
};
const draftDecorationsToCss = {
  BOLD: { "font-weight": "bold" },
  ITALIC: { "font-style": "italic" },
  NOT_BOLD: { "font-weight": "normal" },
  NOT_ITALIC: { "font-style": "normal" }
};
const defaultStyleFnMapper = {
  [RICOS_TEXT_COLOR_TYPE]: (style) => {
    const parsedStyle = dynamicStyleParsers[RICOS_TEXT_COLOR_TYPE](style, isHexColor);
    return parsedStyle ? { color: parsedStyle } : void 0;
  },
  [RICOS_TEXT_HIGHLIGHT_TYPE]: (style) => {
    const parsedStyle = dynamicStyleParsers[RICOS_TEXT_HIGHLIGHT_TYPE](style, isHexColor);
    return parsedStyle ? { backgroundColor: parsedStyle } : void 0;
  },
  [RICOS_FONT_SIZE_TYPE]: (style) => {
    const parsedStyle = dynamicStyleParsers[RICOS_FONT_SIZE_TYPE](style);
    return parsedStyle ? { fontSize: parsedStyle } : void 0;
  },
  [RICOS_BOLD_TYPE]: (style) => style === "NOT_BOLD" ? { fontWeight: "normal" } : void 0,
  [RICOS_ITALIC_TYPE]: (style) => style === "NOT_ITALIC" ? { fontStyle: "normal" } : void 0
};
const defaultStyleSelectionPredicates = {
  [RICOS_TEXT_COLOR_TYPE]: isHexColor,
  [RICOS_TEXT_HIGHLIGHT_TYPE]: isHexColor
};
const getDynamicInlineStyleMapper = (type) => {
  return (config, raw) => {
    const settings = config[RICOS_TO_DRAFT_TYPE[type] || ""] || {};
    const styleParser = dynamicStyleParsers[type];
    const styleSelectionPredicate = settings.styleSelectionPredicate ? (style) => !!styleParser(style, settings.styleSelectionPredicate) : (style) => !!styleParser(style, defaultStyleSelectionPredicates[type]);
    const customStyleFn = settings.customStyleFn ? (style) => settings.customStyleFn(styleParser(style, settings.styleSelectionPredicate)) : defaultStyleFnMapper[type];
    const rawBlocks = getBlocksFromContentState(raw);
    const mapper = rawBlocks.reduce((map, block) => {
      if (block.inlineStyleRanges) {
        block.inlineStyleRanges.filter((range) => styleSelectionPredicate(range.style)).forEach((range) => {
          map[range.style] = (children, { key }) => /* @__PURE__ */ React.createElement("span", { key, style: customStyleFn(range.style) }, children);
        });
      }
      return map;
    }, {});
    return () => mapper;
  };
};

export { INLINE_STYLE_TYPES, defaultStyleFnMapper, draftDecorationsToCss, dynamicStyleParsers, getDynamicInlineStyleMapper, parseStyleByType };
//# sourceMappingURL=viewerUtils.js.map
